import React, { useState, useEffect } from 'react';
import api from '../api';
import { FileText, Package, Plus, ListChecks, CheckSquare, CurrencyDollar, Lightning, Wrench, ShieldCheck } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ReportsView from '../components/ReportsView';
import SolicitarAtendimento from '../components/SolicitarAtendimento';
import MinhasSolicitacoes from '../components/MinhasSolicitacoes';
import ClientFinanceiro from '../components/ClientFinanceiro';
import { useAuth } from '../AuthContext';
import { Badge } from '../components/ui/badge';

const SUGESTOES_MANUTENCAO = {
  residencial: [
    'Revisao de quadro de disjuntores e aterramento',
    'Verificacao de tomadas e interruptores desgastados',
    'Inspecao de fiacao antiga e pontos de aquecimento',
    'Limpeza de ventiladores e ar condicionado',
    'Teste de diferenciais residuais (DR)'
  ],
  comercial: [
    'Manutencao preventiva do sistema de iluminacao',
    'Revisao de paineis eletricos e contatores',
    'Inspecao termica de conexoes e barramentos',
    'Limpeza de luminarias e troca de lampadas queimadas',
    'Verificacao do sistema de emergencia e sinalizacao',
    'Reaperto geral de contatos eletricos'
  ],
  industrial: [
    'Analise termografica de paineis e motores',
    'Manutencao preventiva de motores e bombas',
    'Revisao de sistemas de automacao e CLPs',
    'Inspecao de cabos e eletrocalhas',
    'Verificacao de sistemas de protecao contra surtos',
    'Limpeza e manutencao de compressores'
  ]
};

const ClientDashboard = () => {
  const { userProfile, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const statsRes = await api.get('/dashboard/stats');
      setStats(statsRes.data);
      const clientsRes = await api.get('/clients');
      const clientData = clientsRes.data.find(c => c.contact_email === userProfile?.email);
      setClient(clientData);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#050505] text-white">Carregando...</div>;

  const availableHours = stats?.available_hours || 0;
  const hEletDisp = stats?.horas_eletricista_disponiveis || 0;
  const hAuxDisp = stats?.horas_auxiliar_disponiveis || 0;

  // Calculate days remaining
  const vigenciaFim = stats?.data_fim_vigencia ? new Date(stats.data_fim_vigencia + 'T00:00:00') : null;
  const hoje = new Date();
  const diasRestantes = vigenciaFim ? Math.max(0, Math.ceil((vigenciaFim - hoje) / (1000 * 60 * 60 * 24))) : 0;

  // Suggestions based on client type
  const clientType = stats?.client_type || client?.client_type || 'comercial';
  const sugestoes = SUGESTOES_MANUTENCAO[clientType] || SUGESTOES_MANUTENCAO.comercial;

  // Recently done services (from solicitacoes_resumo)
  const recentServices = stats?.solicitacoes_resumo?.flatMap(s => s.servicos || []) || [];
  const filteredSugestoes = sugestoes.filter(s => !recentServices.some(r => s.toLowerCase().includes(r.toLowerCase().substring(0, 15))));

  const getStatusLabel = (status) => ({ 'pendente': 'Aguardando', 'atribuida': 'Atribuida', 'em_andamento': 'Em Andamento', 'aguardando_aprovacao': 'Aguardando Aprovacao', 'concluida': 'Concluida', 'cancelada': 'Cancelada' })[status] || status;
  const getStatusColor = (status) => ({ 'pendente': 'bg-yellow-500 text-black', 'atribuida': 'bg-blue-500', 'em_andamento': 'bg-purple-500', 'aguardando_aprovacao': 'bg-orange-500', 'concluida': 'bg-emerald-600', 'cancelada': 'bg-red-500' })[status] || 'bg-zinc-500';

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="bg-[#050505] border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/toolserv-logo.png" alt="Toolserv" className="h-8" />
          <div className="flex items-center gap-4">
            <span className="text-zinc-400 text-sm">{userProfile?.name}</span>
            <button onClick={logout} className="px-4 py-2 bg-[#1E1E1E] text-white border border-[#27272A] rounded-sm hover:bg-[#27272A] transition-colors text-sm" data-testid="logout-button">Sair</button>
          </div>
        </div>
      </header>

      <div className="bg-[#121212] border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-2">
            {[
              { id: 'overview', label: 'Visao Geral', icon: Package },
              { id: 'solicitacoes', label: 'Solicitacoes', icon: Plus },
              { id: 'financeiro', label: 'Financeiro', icon: CurrencyDollar },
              { id: 'reports', label: 'Relatorios', icon: FileText }
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${activeTab === tab.id ? 'border-[#FFB800] text-[#FFB800]' : 'border-transparent text-zinc-400 hover:text-white'}`} data-testid={`tab-${tab.id}`}>
                <tab.icon size={20} />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && (
          <div>
            {/* Alert: Days + Hours remaining */}
            {diasRestantes > 0 && availableHours > 0 && (
              <div className="mb-6 p-4 bg-gradient-to-r from-[#FFB800]/20 to-[#E6A600]/10 border border-[#FFB800]/40 rounded-sm" data-testid="vigencia-alert">
                <div className="flex items-start gap-3">
                  <Lightning size={24} weight="fill" className="text-[#FFB800] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[#FFB800] font-semibold">
                      Voce tem {diasRestantes} dias para utilizar {availableHours.toFixed(1)} horas disponiveis
                    </div>
                    <div className="text-zinc-400 text-sm mt-1">
                      Planeje-se e programe uma atividade preventiva para aproveitar ao maximo seu plano!
                    </div>
                  </div>
                </div>
              </div>
            )}

            {availableHours <= 0 && (
              <div className="mb-6 p-4 bg-red-900/20 border border-red-500/40 rounded-sm">
                <div className="text-red-400 font-semibold">Suas horas acabaram! Entre em contato para renovar seu plano.</div>
              </div>
            )}

            {/* Hours Grid - Separated */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Eletricista Hours */}
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="stat-horas-eletricista">
                <div className="text-zinc-400 text-sm mb-3 flex items-center gap-2">
                  <Lightning size={16} className="text-[#FFB800]" />
                  Horas Eletricista
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-zinc-500 text-xs">Contratadas</div>
                    <div className="text-white font-bold text-xl">{(stats?.horas_eletricista_contratadas || 0).toFixed(1)}h</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 text-xs">Usadas</div>
                    <div className="text-white font-bold text-xl">{(stats?.horas_eletricista_usadas || 0).toFixed(1)}h</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 text-xs">Disponiveis</div>
                    <div className={`font-bold text-xl ${hEletDisp < 3 ? 'text-[#FFB800]' : 'text-[#22C55E]'}`}>{hEletDisp.toFixed(1)}h</div>
                  </div>
                </div>
                {(stats?.horas_eletricista_contratadas || 0) > 0 && (
                  <div className="mt-3 w-full bg-[#27272A] rounded-full h-2">
                    <div className="bg-[#FFB800] h-2 rounded-full" style={{ width: `${Math.min(100, ((stats?.horas_eletricista_usadas || 0) / (stats?.horas_eletricista_contratadas || 1)) * 100)}%` }} />
                  </div>
                )}
              </div>

              {/* Auxiliar Hours */}
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="stat-horas-auxiliar">
                <div className="text-zinc-400 text-sm mb-3 flex items-center gap-2">
                  <Wrench size={16} className="text-blue-400" />
                  Horas Auxiliar
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-zinc-500 text-xs">Contratadas</div>
                    <div className="text-white font-bold text-xl">{(stats?.horas_auxiliar_contratadas || 0).toFixed(1)}h</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 text-xs">Usadas</div>
                    <div className="text-white font-bold text-xl">{(stats?.horas_auxiliar_usadas || 0).toFixed(1)}h</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 text-xs">Disponiveis</div>
                    <div className={`font-bold text-xl ${hAuxDisp < 3 ? 'text-[#FFB800]' : 'text-[#22C55E]'}`}>{hAuxDisp.toFixed(1)}h</div>
                  </div>
                </div>
                {(stats?.horas_auxiliar_contratadas || 0) > 0 && (
                  <div className="mt-3 w-full bg-[#27272A] rounded-full h-2">
                    <div className="bg-blue-400 h-2 rounded-full" style={{ width: `${Math.min(100, ((stats?.horas_auxiliar_usadas || 0) / (stats?.horas_auxiliar_contratadas || 1)) * 100)}%` }} />
                  </div>
                )}
              </div>
            </div>

            {/* Total Hours Summary */}
            <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6 mb-6" data-testid="stat-total-hours">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-zinc-400 text-sm">Total de Horas (Eletricista + Auxiliar)</div>
                  <div className="text-3xl font-bold text-white mt-1" style={{ fontFamily: 'Barlow Condensed' }}>
                    {(stats?.contracted_hours || 0).toFixed(1)}h contratadas | {(stats?.used_hours || 0).toFixed(1)}h usadas | <span className={availableHours < 5 ? 'text-[#FFB800]' : 'text-[#22C55E]'}>{availableHours.toFixed(1)}h disponiveis</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Atendimentos Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="stat-solicitadas">
                <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2"><ListChecks size={16} />Atendimentos Solicitados</div>
                <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>{stats?.total_solicitadas || 0}</div>
              </div>
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="stat-concluidas">
                <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2"><CheckSquare size={16} />Atendimentos Concluidos</div>
                <div className="text-3xl font-bold text-[#22C55E]" style={{ fontFamily: 'Barlow Condensed' }}>{stats?.total_concluidas || 0}</div>
              </div>
            </div>

            {/* Maintenance Suggestions */}
            {availableHours > 0 && (
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6 mb-6" data-testid="maintenance-suggestions">
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck size={20} className="text-[#22C55E]" />
                  <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                    Sugestoes de Manutencao Preventiva
                  </h3>
                </div>
                <p className="text-zinc-500 text-sm mb-3">
                  Com base no perfil da sua empresa ({clientType}), recomendamos:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {filteredSugestoes.slice(0, 4).map((s, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-[#050505] border border-[#27272A] rounded-sm">
                      <Wrench size={14} className="text-[#FFB800] flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300 text-sm">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Atendimentos Table */}
            {stats?.solicitacoes_resumo && stats.solicitacoes_resumo.length > 0 && (
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6 mb-6">
                <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Ultimos Atendimentos</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left text-zinc-400 py-3 px-2 font-medium">Solicitacao</th>
                        <th className="text-left text-zinc-400 py-3 px-2 font-medium">Servicos</th>
                        <th className="text-left text-zinc-400 py-3 px-2 font-medium">Tecnico</th>
                        <th className="text-left text-zinc-400 py-3 px-2 font-medium">Horas</th>
                        <th className="text-left text-zinc-400 py-3 px-2 font-medium">Status</th>
                        <th className="text-left text-zinc-400 py-3 px-2 font-medium">Aprovado por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.solicitacoes_resumo.map((sol, idx) => (
                        <tr key={idx} className="border-b border-[#27272A]/50 hover:bg-[#1E1E1E]/50" data-testid={`resumo-row-${idx}`}>
                          <td className="py-3 px-2">
                            <div className="text-[#FFB800] font-semibold">{sol.numero}</div>
                            {sol.descricao && <div className="text-zinc-500 text-xs mt-0.5 max-w-[200px] truncate">{sol.descricao}</div>}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {sol.servicos?.slice(0, 2).map((s, i) => <span key={i} className="text-xs bg-[#050505] px-1.5 py-0.5 rounded text-zinc-400">{s}</span>)}
                              {sol.servicos?.length > 2 && <span className="text-xs text-zinc-500">+{sol.servicos.length - 2}</span>}
                            </div>
                          </td>
                          <td className="py-3 px-2 text-white">{sol.prestador_nome || '-'}</td>
                          <td className="py-3 px-2 text-[#FFB800] font-semibold">{sol.horas_executadas > 0 ? `${sol.horas_executadas.toFixed(1)}h` : '-'}</td>
                          <td className="py-3 px-2"><Badge className={`text-xs ${getStatusColor(sol.status)}`}>{getStatusLabel(sol.status)}</Badge></td>
                          <td className="py-3 px-2 text-zinc-400">{sol.aprovado_por || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Client Info */}
            {client && (
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
                <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Informacoes do Cliente</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><div className="text-zinc-400">Nome</div><div className="text-white font-medium">{client.name}</div></div>
                  <div><div className="text-zinc-400">Plano</div><div className="text-[#FFB800] font-medium">{client.plano_contratado || 'N/A'}</div></div>
                  <div><div className="text-zinc-400">Endereco</div><div className="text-white font-medium">{client.address}</div></div>
                  <div><div className="text-zinc-400">Tipo</div><div className="text-white font-medium capitalize">{client.client_type}</div></div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'solicitacoes' && client && <MinhasSolicitacoes clientId={client.id} />}
        {activeTab === 'financeiro' && client && <ClientFinanceiro clientId={client.id} />}
        {activeTab === 'reports' && client && <ReportsView clientId={client.id} />}
      </div>
    </div>
  );
};

export default ClientDashboard;
